import { useMutation } from "@tanstack/react-query";
import {
  Check,
  FolderMinus,
  FolderPlus,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { EmptyState, ErrorState, LoadingState } from "@/components/feedback";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { Input, ScrollArea } from "@/components/ui/primitives";
import { keys, useConversations, useProjects, useWorkspace } from "@/hooks/queries";
import { api, queryClient, type NodeOut, type WorkspaceItem } from "@/lib/api";
import { cn, truncate } from "@/lib/utils";
import { useGraph } from "@/stores/graph";
import { useTabs } from "@/stores/tabs";

/** A pending "save these pins as a project" intent (name being typed + ids). */
type Naming = { name: string; ids: string[] };

type ProjectRef = { id: string; name: string };

export function ProjectsPanel({
  embedded,
  onClose,
}: {
  /** Rendered inside a mobile drawer: fill it, and close it (not collapse). */
  embedded?: boolean;
  onClose?: () => void;
} = {}) {
  const [open, setOpen] = useState(true);
  const [naming, setNaming] = useState<Naming | null>(null);

  const createProject = useMutation({
    mutationFn: ({ name, ids }: Naming) =>
      api.request("POST", "/api/projects", { name, item_ids: ids }),
    onSuccess: () => {
      setNaming(null);
      toast.success("Project saved");
      void queryClient.invalidateQueries({ queryKey: keys.projects });
      void queryClient.invalidateQueries({ queryKey: keys.workspace });
    },
    onError: (e) => toast.error("Could not save project", { description: String(e) }),
  });

  if (!embedded && !open) {
    return (
      <Tip label="Open projects" side="left">
        <button
          onClick={() => setOpen(true)}
          className="z-20 flex w-9 shrink-0 flex-col items-center gap-2 border-l border-sidebar-border pt-3 text-muted-foreground hover:text-foreground"
          aria-label="Open projects"
        >
          <PanelRightOpen className="h-4 w-4" />
          <span className="font-mono text-[9px] uppercase tracking-widest [writing-mode:vertical-rl]">projects</span>
        </button>
      </Tip>
    );
  }

  return (
    <aside
      className={cn(
        "flex shrink-0 flex-col bg-sidebar text-sidebar-foreground",
        embedded ? "h-full w-full" : "w-[268px] border-l border-sidebar-border",
      )}
    >
      {/* Header */}
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-sidebar-border/60 px-3">
        <span className="font-display text-[13px] italic text-foreground">Projects</span>
        <div className="ml-auto flex items-center gap-0.5">
          <Tip label="New empty project" side="left">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setNaming({ name: "", ids: [] })}
              aria-label="New empty project"
            >
              <FolderPlus className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <Tip label={embedded ? "Close" : "Collapse"} side="left">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => (embedded ? onClose?.() : setOpen(false))}
              aria-label={embedded ? "Close" : "Collapse"}
            >
              {embedded ? <X className="h-4 w-4" /> : <PanelRightClose className="h-3.5 w-3.5" />}
            </Button>
          </Tip>
        </div>
      </div>

      {naming !== null && (
        <form
          className="flex shrink-0 items-center gap-1.5 border-b border-sidebar-border/60 px-3 py-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (naming.name.trim()) createProject.mutate({ name: naming.name.trim(), ids: naming.ids });
          }}
        >
          <Input
            autoFocus
            value={naming.name}
            onChange={(e) => setNaming({ ...naming, name: e.target.value })}
            placeholder={`Name… (${naming.ids.length} pins)`}
          />
          <Button size="icon" variant="ghost" disabled={!naming.name.trim() || createProject.isPending} type="submit">
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" type="button" onClick={() => setNaming(null)}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </form>
      )}

      <ScrollArea className="min-h-0 flex-1">
        <ProjectsList onNewProject={(naming) => setNaming(naming)} />
      </ScrollArea>
    </aside>
  );
}

/** The data-dependent body: groups pins into Project cards, per-conversation
 * cards, and a loose "Unsorted" tray. Split out from the header/naming-form
 * chrome above so a loading/error read never hides the panel's own controls. */
function ProjectsList({ onNewProject }: { onNewProject: (naming: Naming) => void }) {
  const projects = useProjects();
  const workspace = useWorkspace();
  const conversations = useConversations();

  if (workspace.isLoading) {
    return <LoadingState variant="skeleton" rows={4} className="p-3" />;
  }
  if (workspace.isError) {
    return (
      <ErrorState
        error={workspace.error}
        onRetry={() => void workspace.refetch()}
        label="pins"
        className="p-4"
      />
    );
  }

  const items = workspace.data ?? [];
  const convById = new Map((conversations.data ?? []).map((c) => [c.id, c]));
  const projectRefs: ProjectRef[] = (projects.data ?? []).map((p) => ({ id: p.id, name: p.name }));

  // Three tiers: pins live in a Project, or under the conversation they were
  // pinned into, or (no conversation) in the loose "Unsorted" bucket.
  const unprojected = items.filter((i) => !i.project_id);
  const byConv = new Map<string, WorkspaceItem[]>();
  const unsorted: WorkspaceItem[] = [];
  for (const it of unprojected) {
    if (it.conversation_id && convById.has(it.conversation_id)) {
      const arr = byConv.get(it.conversation_id) ?? [];
      arr.push(it);
      byConv.set(it.conversation_id, arr);
    } else {
      unsorted.push(it); // no conversation, or its conversation was deleted
    }
  }
  const convGroups = [...byConv.entries()]
    .map(([cid, its]) => ({ conv: convById.get(cid)!, items: its }))
    .sort((a, b) => (b.conv.updated_at ?? "").localeCompare(a.conv.updated_at ?? ""));

  const isEmpty = (projects.data?.length ?? 0) === 0 && convGroups.length === 0 && unsorted.length === 0;

  if (isEmpty) {
    return (
      <EmptyState
        icon={FolderPlus}
        title="No pins yet"
        description="Pin nodes with the inspector's Pin button — they collect here. Hover a pin and use the folder button to add it to a project."
      />
    );
  }

  return (
    <div className="space-y-4 p-3">
      {(projects.data ?? []).map((p) => (
        <ProjectCard key={p.id} id={p.id} name={p.name} items={p.items} projects={projectRefs} />
      ))}

      {/* Pins grouped by the conversation they were saved into. */}
      {convGroups.map(({ conv, items: its }) => (
        <ConversationCard
          key={conv.id}
          title={conv.title}
          items={its}
          projects={projectRefs}
          onNewProject={(ids) => onNewProject({ name: conv.title, ids })}
        />
      ))}

      {/* Loose pins — saved with no conversation open. */}
      {unsorted.length > 0 && (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-2.5">
          <div className="mb-2 flex items-center gap-2">
            <span className="font-display text-[13px] text-muted-foreground">Unsorted pins</span>
            <span className="font-mono text-[10px] text-muted-foreground">{unsorted.length}</span>
            <Tip label="Group all unsorted pins into a new project" side="left">
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto"
                onClick={() => onNewProject({ name: "", ids: unsorted.map((i) => i.id) })}
              >
                <FolderPlus className="h-3.5 w-3.5" /> Group all
              </Button>
            </Tip>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {unsorted.map((it) => (
              <CharmTile
                key={it.id}
                item={it}
                removable
                projects={projectRefs}
                onNewProject={() => onNewProject({ name: "", ids: [it.id] })}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ConversationCard({
  title,
  items,
  projects,
  onNewProject,
}: {
  title: string;
  items: WorkspaceItem[];
  projects: ProjectRef[];
  onNewProject: (itemIds: string[]) => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-2.5">
      <div className="mb-2 flex items-center gap-1.5">
        <MessageSquare className="h-3 w-3 shrink-0 text-primary/60" />
        <span className="truncate font-display text-[13px] text-muted-foreground" title={title}>
          {truncate(title, 28)}
        </span>
        <span className="font-mono text-[10px] text-muted-foreground">{items.length}</span>
        <Tip label="Save these pins as a new project" side="left">
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            onClick={() => onNewProject(items.map((i) => i.id))}
          >
            <FolderPlus className="h-3.5 w-3.5" /> Save
          </Button>
        </Tip>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {items.map((it) => (
          <CharmTile
            key={it.id}
            item={it}
            removable
            projects={projects}
            onNewProject={() => onNewProject([it.id])}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectCard({
  id,
  name,
  items,
  projects,
}: {
  id: string;
  name: string;
  items: WorkspaceItem[];
  projects: ProjectRef[];
}) {
  const del = useMutation({
    mutationFn: () => api.request("DELETE", `/api/projects/${id}`),
    onSuccess: () => {
      toast.success("Project removed");
      void queryClient.invalidateQueries({ queryKey: keys.projects });
      void queryClient.invalidateQueries({ queryKey: keys.workspace });
    },
  });
  return (
    <div className="group relative rounded-lg border border-border bg-muted/40 p-2.5">
      <div className="mb-2 flex items-center gap-2">
        <span className="truncate font-display text-[13px] text-foreground">{name}</span>
        <span className="font-mono text-[10px] text-muted-foreground">{items.length}</span>
        <Tip label="Delete project (its pins survive, ungrouped)" side="left">
          <button
            onClick={() => del.mutate()}
            aria-label="Delete project"
            className="ml-auto rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </Tip>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {items.map((it) => (
          <CharmTile
            key={it.id}
            item={it}
            removable
            detachable
            // Offer the other projects so a pin can be moved directly.
            projects={projects.filter((p) => p.id !== id)}
          />
        ))}
        {items.length === 0 && (
          <p className="col-span-2 py-2 text-center text-2xs text-muted-foreground">
            Empty — hover any pin and use its folder button to add it here.
          </p>
        )}
      </div>
    </div>
  );
}

function CharmTile({
  item,
  removable,
  detachable,
  projects,
  onNewProject,
}: {
  item: WorkspaceItem;
  removable?: boolean;
  /** Show a "remove from this project" control. */
  detachable?: boolean;
  /** Projects this pin can be moved into (opens the picker menu). */
  projects?: ProjectRef[];
  onNewProject?: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const unpin = useMutation({
    mutationFn: () => api.request("DELETE", `/api/workspace/items/${item.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.workspace });
      void queryClient.invalidateQueries({ queryKey: keys.projects });
    },
  });
  const assign = useMutation({
    mutationFn: (projectId: string | null) =>
      api.request("PATCH", `/api/workspace/items/${item.id}`, { project_id: projectId }),
    onSuccess: (_d, projectId) => {
      setMenu(false);
      toast.success(projectId ? "Added to project" : "Removed from project");
      void queryClient.invalidateQueries({ queryKey: keys.workspace });
      void queryClient.invalidateQueries({ queryKey: keys.projects });
    },
    onError: (e) => toast.error("Could not move pin", { description: String(e) }),
  });
  const open = async () => {
    if (!item.ref_id) return;
    if (item.body === "orbit") {
      useTabs.getState().activate("orbits");
      useGraph.getState().requestFocus(item.ref_id);
      return;
    }
    try {
      const node = await api.request<NodeOut>("GET", `/api/nodes/${item.ref_id}`);
      if (node.type === "document" && node.document_id) {
        useTabs.getState().openReader(node.document_id, node.label);
      } else {
        useTabs.getState().activate("graph");
        useGraph.getState().requestFocus(node.id);
      }
    } catch {
      // The pinned node was deleted — drop the stale pin instead of asking the
      // canvas to focus a dead id (which used to spin the focus poller forever).
      toast.error("That pinned item was deleted", { description: "Removing the stale pin." });
      unpin.mutate();
    }
  };
  const canAssign = (projects?.length ?? 0) > 0 || !!onNewProject;
  return (
    <div className="group/charm relative">
      <button
        onClick={() => void open()}
        title={item.title}
        className="grid aspect-square w-full place-items-center rounded-md border border-border bg-card/60 p-1.5 transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:bg-accent"
      >
        <span className="absolute inset-1 rounded border border-primary/10" />
        <span className="line-clamp-3 text-center text-[10px] leading-tight text-muted-foreground group-hover/charm:text-foreground">
          {truncate(item.title, 38)}
        </span>
      </button>
      {removable && (
        <Tip label="Unpin">
          <button
            onClick={() => unpin.mutate()}
            aria-label="Unpin"
            className="absolute -right-1 -top-1 hidden rounded-full border border-border bg-card p-0.5 text-muted-foreground hover:text-destructive group-hover/charm:block"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </Tip>
      )}
      {canAssign && (
        <Tip label="Add to project">
          <button
            onClick={() => setMenu((m) => !m)}
            aria-label="Add to project"
            className={cn(
              "absolute -bottom-1 -right-1 rounded-full border border-border bg-card p-0.5 text-muted-foreground hover:text-primary group-hover/charm:block",
              menu ? "block text-primary" : "hidden",
            )}
          >
            <FolderPlus className="h-2.5 w-2.5" />
          </button>
        </Tip>
      )}
      {detachable && (
        <Tip label="Remove from this project">
          <button
            onClick={() => assign.mutate(null)}
            aria-label="Remove from this project"
            className="absolute -bottom-1 -left-1 hidden rounded-full border border-border bg-card p-0.5 text-muted-foreground hover:text-destructive group-hover/charm:block"
          >
            <FolderMinus className="h-2.5 w-2.5" />
          </button>
        </Tip>
      )}
      {menu && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1.5 min-w-[140px] rounded-md border border-border bg-popover p-1 shadow-lg">
          <p className="px-2 pb-1 pt-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            Add to project
          </p>
          {(projects ?? []).map((p) => (
            <button
              key={p.id}
              onClick={() => assign.mutate(p.id)}
              disabled={assign.isPending}
              className="block w-full truncate rounded px-2 py-1 text-left text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {truncate(p.name, 24)}
            </button>
          ))}
          {onNewProject && (
            <button
              onClick={() => {
                setMenu(false);
                onNewProject();
              }}
              className="block w-full truncate rounded px-2 py-1 text-left text-[11px] text-primary/80 hover:bg-accent hover:text-primary"
            >
              + New project…
            </button>
          )}
        </div>
      )}
    </div>
  );
}
